import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Save, Lock, User as UserIcon, Camera, Trash2, Briefcase } from "lucide-react";
import { getMe, atualizarMe, uploadFotoPerfil, removerFotoPerfil } from "../services/me";
import { useAuthStore } from "../store/authStore";
import { Avatar } from "../components/Avatar";

const REAL = new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" });
const DATA_BR = new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric" });

export function Perfil() {
  const qc = useQueryClient();
  const setAuth = useAuthStore((s) => s.setAuth);
  const token = useAuthStore((s) => s.token);
  const refreshToken = useAuthStore((s) => s.refreshToken);

  const { data: me, isLoading } = useQuery({ queryKey: ["me"], queryFn: getMe });

  const [nome, setNome] = useState("");
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [dataNascimento, setDataNascimento] = useState("");
  const [fusoHorario, setFusoHorario] = useState("");
  const [senhaAtual, setSenhaAtual] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [novaSenha2, setNovaSenha2] = useState("");
  const [msg, setMsg] = useState<{ tipo: "ok" | "erro"; texto: string } | null>(null);
  const [hidratado, setHidratado] = useState(false);

  // Sincroniza o form com `me` uma única vez (quando carrega)
  useEffect(() => {
    if (me && !hidratado) {
      setNome(me.nome);
      setEmail(me.email);
      setTelefone(me.telefone || "");
      setWhatsapp(me.whatsapp || "");
      setDataNascimento(me.dataNascimento ? me.dataNascimento.slice(0, 10) : "");
      setFusoHorario(me.fusoHorario || "");
      setHidratado(true);
    }
  }, [me, hidratado]);

  const dadosM = useMutation({
    mutationFn: () => atualizarMe({
      nome, email,
      telefone: telefone.trim() || null,
      whatsapp: whatsapp.trim() || null,
      dataNascimento: dataNascimento || null,
      fusoHorario: fusoHorario.trim() || null,
    }),
    onSuccess: (u) => {
      setMsg({ tipo: "ok", texto: "Dados atualizados" });
      qc.invalidateQueries({ queryKey: ["me"] });
      // Atualiza o store pra refletir no header
      if (token && refreshToken) {
        setAuth({ ...(u as any), perfil: u.perfil as any }, token, refreshToken);
      }
    },
    onError: (e: any) => setMsg({ tipo: "erro", texto: e?.response?.data?.error?.message || "Erro ao salvar" }),
  });

  const senhaM = useMutation({
    mutationFn: () => atualizarMe({ senhaAtual, novaSenha }),
    onSuccess: () => {
      setMsg({ tipo: "ok", texto: "Senha trocada com sucesso" });
      setSenhaAtual("");
      setNovaSenha("");
      setNovaSenha2("");
    },
    onError: (e: any) => setMsg({ tipo: "erro", texto: e?.response?.data?.error?.message || "Erro ao trocar senha" }),
  });

  if (isLoading) return <p className="text-sm text-slate-400">Carregando…</p>;

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-xl font-bold text-slate-800">Meu perfil</h1>
        <p className="text-sm text-slate-500 mt-1">Atualize seus dados e a sua senha.</p>
      </div>

      {msg && (
        <div className={`px-4 py-2 rounded-lg text-sm ${msg.tipo === "ok" ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "bg-red-50 text-red-700 border border-red-200"}`}>
          {msg.texto}
        </div>
      )}

      <FotoPerfilSection me={me} />

      <section className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
          <UserIcon size={16} /> Dados pessoais
        </h2>
        <form
          onSubmit={(e) => { e.preventDefault(); setMsg(null); dadosM.mutate(); }}
          className="space-y-3"
        >
          <div>
            <label className="text-xs text-slate-600">Nome</label>
            <input type="text" required value={nome} onChange={(e) => setNome(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500" />
          </div>
          <div>
            <label className="text-xs text-slate-600">E-mail</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-600">Telefone</label>
              <input type="tel" value={telefone} onChange={(e) => setTelefone(e.target.value)} placeholder="(00) 0000-0000" className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500" />
            </div>
            <div>
              <label className="text-xs text-slate-600">WhatsApp</label>
              <input type="tel" value={whatsapp} onChange={(e) => setWhatsapp(e.target.value)} placeholder="(00) 00000-0000" className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500" />
            </div>
            <div>
              <label className="text-xs text-slate-600">Data de nascimento</label>
              <input type="date" value={dataNascimento} onChange={(e) => setDataNascimento(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500" />
              <p className="text-[10px] text-slate-400 mt-0.5">Aparece como aniversário no calendário da equipe.</p>
            </div>
            <div>
              <label className="text-xs text-slate-600">Fuso horário</label>
              <input type="text" value={fusoHorario} onChange={(e) => setFusoHorario(e.target.value)} placeholder="America/Sao_Paulo" className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500 font-mono" />
            </div>
          </div>
          <div className="text-xs text-slate-500">Perfil: <strong className="capitalize">{me?.perfil}</strong></div>
          <div className="flex justify-end">
            <button type="submit" disabled={dadosM.isPending} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
              <Save size={14} /> {dadosM.isPending ? "Salvando…" : "Salvar"}
            </button>
          </div>
        </form>
      </section>

      {/* Dados profissionais — só leitura. Editados por admin/gestor. */}
      {me?.perfil !== "cliente" && (me?.cargo || me?.entrouEmpresaEm || me?.custoHora != null) && (
        <section className="bg-white rounded-xl border border-slate-200 p-5">
          <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
            <Briefcase size={16} /> Dados profissionais
            <span className="text-[10px] text-slate-400 font-normal">— definidos pela gestão</span>
          </h2>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {me.cargo && (
              <>
                <dt className="text-slate-500">Cargo</dt>
                <dd className="text-slate-800">{me.cargo}</dd>
              </>
            )}
            {me.entrouEmpresaEm && (
              <>
                <dt className="text-slate-500">Na empresa desde</dt>
                <dd className="text-slate-800">{DATA_BR.format(new Date(me.entrouEmpresaEm))}</dd>
              </>
            )}
            {me.custoHora != null && (
              <>
                <dt className="text-slate-500">Custo/hora</dt>
                <dd className="text-slate-800 font-mono">{REAL.format(me.custoHora)}</dd>
              </>
            )}
          </dl>
        </section>
      )}

      <section className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
          <Lock size={16} /> Trocar senha
        </h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setMsg(null);
            if (novaSenha !== novaSenha2) { setMsg({ tipo: "erro", texto: "As novas senhas não conferem" }); return; }
            senhaM.mutate();
          }}
          className="space-y-3"
        >
          <div>
            <label className="text-xs text-slate-600">Senha atual</label>
            <input type="password" required value={senhaAtual} onChange={(e) => setSenhaAtual(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500" />
          </div>
          <div>
            <label className="text-xs text-slate-600">Nova senha (mín. 6)</label>
            <input type="password" required minLength={6} value={novaSenha} onChange={(e) => setNovaSenha(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500" />
          </div>
          <div>
            <label className="text-xs text-slate-600">Confirmar nova senha</label>
            <input type="password" required value={novaSenha2} onChange={(e) => setNovaSenha2(e.target.value)} className="w-full px-3 py-2 rounded-lg border border-slate-300 outline-none text-sm focus:border-blue-500" />
          </div>
          <div className="flex justify-end">
            <button type="submit" disabled={senhaM.isPending} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50">
              <Save size={14} /> {senhaM.isPending ? "Trocando…" : "Trocar senha"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function FotoPerfilSection({ me }: { me: any }) {
  const qc = useQueryClient();
  const setAuth = useAuthStore((s) => s.setAuth);
  const token = useAuthStore((s) => s.token);
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const fileRef = useRef<HTMLInputElement>(null);

  const uploadM = useMutation({
    mutationFn: (f: File) => uploadFotoPerfil(f),
    onSuccess: async () => {
      const u = await getMe();
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["presence"] });
      qc.invalidateQueries({ queryKey: ["usuarios-ativos"] });
      qc.invalidateQueries({ queryKey: ["usuarios"] });
      qc.invalidateQueries({ queryKey: ["chat-mensagens"] });
      if (token && refreshToken) setAuth({ ...(u as any), perfil: u.perfil as any }, token, refreshToken);
    },
  });

  const removerM = useMutation({
    mutationFn: () => removerFotoPerfil(),
    onSuccess: async () => {
      const u = await getMe();
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["presence"] });
      qc.invalidateQueries({ queryKey: ["usuarios-ativos"] });
      qc.invalidateQueries({ queryKey: ["usuarios"] });
      if (token && refreshToken) setAuth({ ...(u as any), perfil: u.perfil as any }, token, refreshToken);
    },
  });

  return (
    <section className="bg-white rounded-xl border border-slate-200 p-5">
      <h2 className="text-sm font-semibold text-slate-700 flex items-center gap-2 mb-3">
        <Camera size={16} /> Foto do perfil
      </h2>
      <div className="flex items-center gap-4">
        <Avatar nome={me?.nome} fotoUrl={me?.fotoUrl} tamanho={80} bgFallback="bg-blue-500" textFallback="text-white" />
        <div className="flex-1 space-y-2">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploadM.isPending}
              className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded flex items-center gap-1.5 disabled:opacity-50"
            >
              <Camera size={14} /> {uploadM.isPending ? "Enviando…" : "Trocar foto"}
            </button>
            {me?.fotoUrl && (
              <button
                onClick={() => removerM.mutate()}
                disabled={removerM.isPending}
                className="text-sm bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded flex items-center gap-1.5"
              >
                <Trash2 size={14} /> Remover
              </button>
            )}
          </div>
          <p className="text-[11px] text-slate-500">PNG, JPG ou GIF. Máximo 5MB. A foto aparece no chat, no menu e nas listas.</p>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) uploadM.mutate(f);
            if (fileRef.current) fileRef.current.value = "";
          }}
        />
      </div>
    </section>
  );
}
